import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { faPencil } from '@fortawesome/pro-regular-svg-icons/faPencil';
import { faPlay } from '@fortawesome/pro-solid-svg-icons/faPlay';
import { Button, Icon, Tag, Tooltip } from '@rocketlane/ui';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { ClawAgentCard } from 'components/organisms/nitro-claw/claw-agent-card';
import { NITRO_BREWS_MESH } from 'components/organisms/nitro-claw/nitro-claw-mesh.config';
import { useTranslate } from 'hooks/use-translation';
import { MeshSphere } from 'vendor/meshy';
const ClawNitroBrewCard = ({ brew, canEdit, onOpen, onEdit, onRun })=>{
    var _brew_plan;
    const translate = useTranslate();
    const description = ((_brew_plan = brew.plan) === null || _brew_plan === void 0 ? void 0 : _brew_plan.trim()) || brew.name;
    const editLabel = translate('nitro_claw.nitro_brews.edit', undefined, 'Edit');
    const runLabel = translate('nitro_claw.nitro_brews.run', undefined, 'Run');
    return /*#__PURE__*/ _jsx(Tooltip, {
        content: description,
        side: "bottom",
        delayDuration: 700,
        children: /*#__PURE__*/ _jsx(ClawAgentCard, {
            title: brew.name,
            dataCy: `claw-nitro-brew-list.card.${brew.id}`,
            onClick: ()=>onOpen(brew),
            icon: (isHovered)=>/*#__PURE__*/ _jsx(MeshSphere, {
                    colors: NITRO_BREWS_MESH.colors,
                    meshId: brew.id,
                    size: 24,
                    animated: isHovered
                }),
            actions: /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    canEdit ? /*#__PURE__*/ _jsx(Button, {
                        variant: "secondary",
                        size: "small",
                        "aria-label": editLabel,
                        leftIcon: /*#__PURE__*/ _jsx(Icon, {
                            icon: faPencil
                        }),
                        onClick: (event)=>{
                            event.stopPropagation();
                            onEdit(brew);
                        },
                        "data-cy": `claw-nitro-brew-list.edit.${brew.id}`
                    }) : null,
                    /*#__PURE__*/ _jsx(Button, {
                        type: "button",
                        variant: "primary",
                        size: "small",
                        "aria-label": runLabel,
                        leftIcon: /*#__PURE__*/ _jsx(Icon, {
                            icon: faPlay
                        }),
                        onClick: (event)=>{
                            event.stopPropagation();
                            onRun(brew);
                        },
                        "data-cy": `claw-nitro-brew-list.run.${brew.id}`
                    })
                ]
            })
        })
    });
};
const ClawNitroBrewSection = ({ titleId, titleDefault, brews, canEdit, onOpen, onEdit, onRun })=>{
    if (brews.length === 0) {
        return null;
    }
    return /*#__PURE__*/ _jsxs("section", {
        className: "cp-flex cp-w-full cp-flex-col cp-gap-[var(--spacing-04)]",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "cp-flex cp-items-center cp-gap-[var(--spacing-03)]",
                children: [
                    /*#__PURE__*/ _jsx("h2", {
                        className: "cp-m-0 body-short-01 !cp-font-semibold cp-text-[var(--text-secondary)]",
                        "data-cy": `claw-nitro-brew-list.heading.${canEdit ? 'mine' : 'shared'}`,
                        children: /*#__PURE__*/ _jsx(FormattedMessage, {
                            id: titleId,
                            defaultMessage: titleDefault
                        })
                    }),
                    /*#__PURE__*/ _jsx(Tag, {
                        variant: "pill",
                        color: "gray",
                        height: 20,
                        fontSize: 12,
                        "data-cy": `claw-nitro-brew-list.count.${canEdit ? 'mine' : 'shared'}`,
                        children: brews.length
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                "data-cy": `claw-nitro-brew-list.grid.${canEdit ? 'mine' : 'shared'}`,
                className: "cp-grid cp-grid-cols-3 cp-gap-[var(--spacing-05)]",
                children: brews.map((brew)=>/*#__PURE__*/ _jsx(ClawNitroBrewCard, {
                        brew: brew,
                        canEdit: canEdit,
                        onOpen: onOpen,
                        onEdit: onEdit,
                        onRun: onRun
                    }, brew.id))
            })
        ]
    });
};
export const ClawNitroBrewSections = ({ agents, currentUserId, onOpen, onEdit, onRun })=>{
    const { mine, shared } = useMemo(()=>{
        const byUpdatedDesc = (a, b)=>b.updated_at.localeCompare(a.updated_at);
        const owned = [];
        const sharedWithMe = [];
        agents.filter((agent)=>!agent.is_deleted).forEach((agent)=>{
            if (agent.created_by === currentUserId) {
                owned.push(agent);
            } else {
                sharedWithMe.push(agent);
            }
        });
        owned.sort(byUpdatedDesc);
        sharedWithMe.sort(byUpdatedDesc);
        return {
            mine: owned,
            shared: sharedWithMe
        };
    }, [
        agents,
        currentUserId
    ]);
    return /*#__PURE__*/ _jsxs("div", {
        className: "cp-flex cp-w-full cp-flex-col cp-gap-[var(--spacing-09)]",
        children: [
            /*#__PURE__*/ _jsx(ClawNitroBrewSection, {
                titleId: "nitro_claw.nitro_brews.created_by_me",
                titleDefault: "Created by me",
                brews: mine,
                canEdit: true,
                onOpen: onOpen,
                onEdit: onEdit,
                onRun: onRun
            }),
            /*#__PURE__*/ _jsx(ClawNitroBrewSection, {
                titleId: "nitro_claw.nitro_brews.shared_with_me",
                titleDefault: "Shared with me",
                brews: shared,
                canEdit: false,
                onOpen: onOpen,
                onEdit: onEdit,
                onRun: onRun
            })
        ]
    });
};
